import currencyFormatter from '~/utils/currencyFormatter';

import {
  ALL_SEVERITY_FILTER,
  BEST_COMPETITOR_LABEL,
  HEATMAP_COLORS,
  LE_SUPPLIER_LABEL,
  OUT_OF_PARITY_HEALTHY_THRESHOLD,
  PARITY_THRESHOLD_MINOR,
  PARITY_THRESHOLD_MODERATE,
  PARITY_THRESHOLD_SEVERE,
  SEVERITY_COLORS,
  SEVERITY_ORDER,
} from './constants';
import {
  DailyRow,
  DateRange,
  MatrixDisplayMode,
  ParityAnalysis,
  ParityRating,
  RevenueHooks,
  TRACKED_OTAS,
} from './types';

export function isParityHealthy(analysis: ParityAnalysis): boolean {
  return analysis.parityRating === 'GOOD' && analysis.outOfParityCount <= OUT_OF_PARITY_HEALTHY_THRESHOLD;
}

export function fmtShortDate(d: string): string {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
}

export function fmtMonthHeader(d: string): string {
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
}

export type { ParityRating };

const SCORE_TIERS: ReadonlyArray<{ min: number; rating: ParityRating; color: string }> = [
  { min: 80, rating: 'GOOD', color: SEVERITY_COLORS.in_parity },
  { min: 60, rating: 'FAIR', color: SEVERITY_COLORS.minor },
  { min: 40, rating: 'POOR', color: SEVERITY_COLORS.moderate },
  { min: 0, rating: 'CRITICAL', color: SEVERITY_COLORS.severe },
];

export function scoreColor(score: number): string {
  return (SCORE_TIERS.find((t) => score >= t.min) ?? SCORE_TIERS[SCORE_TIERS.length - 1]).color;
}

export function parseCommaNumber(s: string | undefined): number {
  return Number(s?.replace(/,/g, ''));
}

export function computeImpressionParityScore(hooks: RevenueHooks): number | null {
  const total = parseCommaNumber(hooks.totalImpressions);
  const outOfParity = parseCommaNumber(hooks.impressionsOutOfParity);
  if (!total || isNaN(total) || total === 0) {
    return null;
  }
  if (isNaN(outOfParity)) {
    return null;
  }
  return Math.round(((total - outOfParity) / total) * 100);
}

export function scoreToRating(score: number): ParityRating {
  return (SCORE_TIERS.find((t) => score >= t.min) ?? SCORE_TIERS[SCORE_TIERS.length - 1]).rating;
}

export function computeInParityImpressions(hooks: RevenueHooks): string {
  const total = parseCommaNumber(hooks.totalImpressions);
  const outOfParity = parseCommaNumber(hooks.impressionsOutOfParity);
  if (isNaN(total) || isNaN(outOfParity)) {
    return '—';
  }
  return Math.round(total - outOfParity).toLocaleString();
}

export function countDaysWithoutLeRates(dailyDetail: Array<DailyRow>): number {
  return dailyDetail.filter((d) => d.lePrice === null && d.bestCompetitor !== null).length;
}

export function computeYDomain(
  dailyDetail: Array<DailyRow>,
  activeOtas: ReadonlySet<string>,
): { yMin: number; yMax: number } {
  const allValues: Array<number> = [];
  for (const d of dailyDetail) {
    if (d.lePrice !== null) {
      allValues.push(d.lePrice);
    }
    for (const [ota, price] of Object.entries(d.allPrices)) {
      if (activeOtas.has(ota)) {
        allValues.push(price);
      }
    }
  }
  const padding = allValues.length > 0 ? (Math.max(...allValues) - Math.min(...allValues)) * 0.05 : 100;
  const yMin = allValues.length > 0 ? Math.max(0, Math.floor(Math.min(...allValues) - padding)) : 0;
  const yMax = allValues.length > 0 ? Math.ceil(Math.max(...allValues) + padding) : 1000;
  return { yMin, yMax };
}

export function buildChartData(
  dailyDetail: Array<DailyRow>,
  leColumn: string,
): Array<Record<string, string | number | null>> {
  return dailyDetail.map((d) => {
    const point: Record<string, string | number | null> = {
      date: fmtShortDate(d.date),
      [leColumn]: d.lePrice,
      [BEST_COMPETITOR_LABEL]: d.bestCompetitorPrice,
    };
    for (const ota of TRACKED_OTAS) {
      point[ota] = d.allPrices[ota] ?? null;
    }
    return point;
  });
}

export function deriveUniqueCompetitors(dailyDetail: Array<DailyRow>): Array<string> {
  const otas = new Set<string>();
  for (const d of dailyDetail) {
    for (const ota of Object.keys(d.allPrices)) {
      otas.add(ota);
    }
  }
  return [...otas].sort();
}

export function deriveUniqueSeverities(dateRanges: Array<DateRange>): Array<string> {
  return [...new Set(dateRanges.map((r) => r.severity))].sort(
    (x, y) => SEVERITY_ORDER.indexOf(x) - SEVERITY_ORDER.indexOf(y),
  );
}

export function filterDateRanges(dateRanges: Array<DateRange>, severityFilter: string): Array<DateRange> {
  if (severityFilter === ALL_SEVERITY_FILTER) {
    return dateRanges;
  }
  return dateRanges.filter((r) => r.severity === severityFilter);
}

// channelVisibility maps OTA → true/false (opt-in)
export function deriveActiveOtas(channelVisibility: Record<string, boolean>): Set<string> {
  return new Set(TRACKED_OTAS.filter((ota) => channelVisibility[ota]));
}

// competitorVisibility maps competitor → true/false; default is "all visible" (opt-out)
export function deriveActiveTableOtas(
  competitorVisibility: Record<string, boolean>,
  uniqueCompetitors: Array<string>,
): Set<string> {
  const hidden = new Set(uniqueCompetitors.filter((c) => !competitorVisibility[c]));
  return new Set(TRACKED_OTAS.filter((ota) => !hidden.has(ota)));
}

export function deriveVisibleOtas(hiddenSuppliers: ReadonlySet<string>): Set<string> {
  return new Set(TRACKED_OTAS.filter((ota) => !hiddenSuppliers.has(ota)));
}

export function getHeatmapColors(
  price: number | null,
  bestPrice: number | null,
): { bgcolor: string; color: string } | null {
  if (price == null || bestPrice == null || bestPrice === 0) {
    return null;
  }

  const pctAboveBest = ((price - bestPrice) / bestPrice) * 100;

  if (pctAboveBest <= PARITY_THRESHOLD_MINOR * 100) {
    return HEATMAP_COLORS.good;
  }
  if (pctAboveBest <= PARITY_THRESHOLD_MODERATE * 100) {
    return HEATMAP_COLORS.minor;
  }
  if (pctAboveBest <= PARITY_THRESHOLD_SEVERE * 100) {
    return HEATMAP_COLORS.moderate;
  }
  return HEATMAP_COLORS.severe;
}

/**
 * (price − lowest) / lowest × 100, rounded to one decimal.
 * Uses cent-scaled integers so values like 101.05 vs 100 do not lose half-up rounding to float noise.
 */
function matrixPercentDiffOneDecimal(price: number, lowestPrice: number): number {
  const priceCents = Math.round(price * 100);
  const lowestCents = Math.round(lowestPrice * 100);
  const deltaCents = priceCents - lowestCents;
  return Math.round((1000 * deltaCents) / lowestCents) / 10;
}

export function formatMatrixCellDisplay(
  mode: MatrixDisplayMode,
  currency: string,
  price: number | null,
  lowestPrice: number | null,
): string {
  if (price == null) {
    return '-';
  }
  if (mode === 'absolute') {
    return currencyFormatter(currency, price, 0);
  }
  if (lowestPrice == null || lowestPrice === 0) {
    return '-';
  }
  if (mode === 'priceDiff') {
    const delta = price - lowestPrice;
    if (delta > 0) {
      return `+${currencyFormatter(currency, delta, 0)}`;
    }
    if (delta < 0) {
      return currencyFormatter(currency, delta, 0);
    }
    return currencyFormatter(currency, 0, 0);
  }
  const pct = matrixPercentDiffOneDecimal(price, lowestPrice);
  if (pct > 0) {
    return `+${pct}%`;
  }
  if (pct < 0) {
    return `${pct}%`;
  }
  return '0%';
}

export function matrixCellExportValue(
  mode: MatrixDisplayMode,
  price: number | null,
  lowestPrice: number | null,
): number | null {
  if (price == null) {
    return null;
  }
  if (mode === 'absolute') {
    return price;
  }
  if (lowestPrice == null || lowestPrice === 0) {
    return null;
  }
  if (mode === 'priceDiff') {
    return price - lowestPrice;
  }
  return matrixPercentDiffOneDecimal(price, lowestPrice);
}

export function buildSupplierPriceMap(dailyDetail: Array<DailyRow>): Record<string, Record<string, number | null>> {
  const map: Record<string, Record<string, number | null>> = {};

  map[LE_SUPPLIER_LABEL] = {};
  for (const d of dailyDetail) {
    map[LE_SUPPLIER_LABEL][d.date] = d.lePrice;
  }

  for (const ota of TRACKED_OTAS) {
    map[ota] = {};
    for (const d of dailyDetail) {
      map[ota][d.date] = d.allPrices[ota] ?? null;
    }
  }

  map[BEST_COMPETITOR_LABEL] = {};
  for (const d of dailyDetail) {
    map[BEST_COMPETITOR_LABEL][d.date] = d.bestCompetitorPrice;
  }

  return map;
}

export function buildMonthGroups(dailyDetail: Array<DailyRow>): Array<{ month: string; count: number }> {
  const groups: Array<{ month: string; count: number }> = [];
  let lastMonth = '';

  for (const d of dailyDetail) {
    const month = fmtMonthHeader(d.date);
    if (month !== lastMonth) {
      groups.push({ month, count: 1 });
      lastMonth = month;
    } else {
      groups[groups.length - 1].count++;
    }
  }

  return groups;
}

export function deriveLowestPriceByDate(dailyDetail: Array<DailyRow>): Record<string, number | null> {
  const map: Record<string, number | null> = {};
  for (const d of dailyDetail) {
    const candidates = [d.lePrice, d.bestCompetitorPrice].filter((p): p is number => p != null);
    map[d.date] = candidates.length > 0 ? Math.min(...candidates) : null;
  }
  return map;
}
